import { useState, useEffect, useMemo, useRef } from 'react';
import { MAX_PRICE, MIN_PRICE } from '~/utils/moneyBounds';
import { Truck, TramFront } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui';
import { MarketPricePill } from '~/components/ui/MarketPricePill';
import type { OrderInfo } from 'src/pages/order-builder/OrderBuider';
import { CartCard } from './CartCard';
import { transportOfAnotherCart } from '~/utils/order';
import { useCartsAddedSinceOpen } from '~/pages/order-builder/useCartsAddedSinceOpen';
import { CannedTallyCartCard } from './CannedTallyCartCard';
import type {
  Cart,
  PlatformProductLength,
  TransportVehicle,
  UnitCode,
} from '~/graphql/graphql';
import { CartSummary } from './CartSummary';
import { formatUnitAmount, formatUnitPrice, priceUnitLabel } from '~/utils';
import {
  formatPricedMarketMid,
  marketMidPriceIn,
} from '~/utils/marketMidPrice';
import { currencyCodeToSymbolMap } from '~/utils/data';
import { useGetPlatformProductCannedTallies } from '~/api/canned-tally';
import { CANNED_TALLY_VEHICLE_NAMES } from '~/utils/vehicleGroups';
import { useGetBuyerInventoriesForLengths } from '~/api/inventory';
import { useGetPlatformProductById } from '~/api/platformProducts';
import { useGetTransportVehicles } from '~/api/facility';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import { convertPiecesToUnit } from '~/utils/unitConversion';
import {
  buildTallyBundlesByFt,
  getItemWholeFt,
  getPlatformProductDimensions,
  getVehiclePayload,
  wholeFootLength,
} from '~/utils/order';

const TRUCK_LENGTHS_FT = [8, 10, 12, 14, 16];
const RAILCAR_LENGTHS_FT = [8, 10, 12, 14, 16, 18, 20];

type TallyInventorySummary = {
  avgWeeksCovered: number;
  maxTargetWeeks: number;
  totalTargetQuantity: number;
};

export type TallyStockEntry = {
  ft: number;
  stock: number;
  weeksCovered: number | null;
  targetWeeks: number | null;
};

export type TallyStockSummary = {
  byLength: TallyStockEntry[];
  usageRate: number | null;
};

type OrderCalculatorProps = {
  onOrderInfoChange: (name: string, value: string) => void;
  onUserTargetChange?: () => void;
  onTargetOrderSizeBlur?: () => void;
  onSuggestedWeeksToCover: (weeks: number) => void;
  orderInfo: OrderInfo;
  averageNPrice: number | null;
  nCount: number;
  formattedMarketMidPriceWithUnit: string | null;
  carts: Cart[];
  platformProductUnit: UnitCode;
  currency?: string;
  isCannedTally?: boolean;
  platformProductLengths?: PlatformProductLength[];
  platformProductId?: string;
  transportMode?: 'trucks' | 'railcars';
  onTransportModeChange?: (mode: 'trucks' | 'railcars') => void;
  onTargetVehiclesChange?: (v: number) => void;
  onTallyNameChange?: (name: string | undefined) => void;
  transportVehicleCatalog?: TransportVehicle[];
};
export function OrderCalculator({
  onOrderInfoChange,
  onUserTargetChange,
  onTargetOrderSizeBlur,
  onSuggestedWeeksToCover,
  orderInfo,
  averageNPrice,
  nCount,
  formattedMarketMidPriceWithUnit,
  platformProductUnit,
  currency,
  carts = [],
  isCannedTally = false,
  platformProductLengths = [],
  platformProductId,
  transportMode = 'trucks',
  onTransportModeChange,
  onTargetVehiclesChange,
  onTallyNameChange,
  transportVehicleCatalog,
}: Readonly<OrderCalculatorProps>) {
  const { t } = useTranslation('order-builder');
  const [tallyInventorySummary, setTallyInventorySummary] =
    useState<TallyInventorySummary | null>(null);
  const [tallyStockSummary, setTallyStockSummary] =
    useState<TallyStockSummary | null>(null);

  const selectedLocationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );
  const cartsAddedSinceOpen = useCartsAddedSinceOpen(
    carts.map((cart) => cart.id),
    selectedLocationId
  );
  // A cart refused its inherited vehicle on an earlier visit falls back to its own auto-select
  // instead of being offered the same vehicle again.
  const inheritedTransportFor = (cart: Cart) =>
    cartsAddedSinceOpen.has(cart.id)
      ? transportOfAnotherCart(carts, cart)
      : null;
  const inheritedIncoTermFor = (cart: Cart) => {
    const inherited = inheritedTransportFor(cart);
    return inherited ? inherited.incoTerm : null;
  };

  const totalCartItems = carts.reduce((total, cart) => {
    const items = cart.items ?? [];
    const tallyGroupIds = new Set(
      items
        .filter((i) => i.tallyPlatformProduct?.id)
        .map((i) => i.tallyPlatformProduct!.id)
    );
    const regularItemCount = items.filter(
      (i) => !i.tallyPlatformProduct?.id
    ).length;
    return total + tallyGroupIds.size + regularItemCount;
  }, 0);

  const singleLengthCartTotals = (() => {
    if (isCannedTally || platformProductLengths.length !== 1) return null;
    const selectedLengthId = platformProductLengths[0].length.id;
    const filteredItems = (carts ?? [])
      .flatMap((cart) => cart.items ?? [])
      .filter((item) => {
        if (item.platformProductsLengths?.length?.id !== selectedLengthId)
          return false;
        return (
          item.platformProductsLengths?.platformProduct?.id ===
            platformProductId ||
          item.tallyPlatformProduct?.id === platformProductId
        );
      });
    return {
      qty: filteredItems.reduce((sum, item) => sum + item.quantity.value, 0),
      value: filteredItems.reduce(
        (sum, item) =>
          sum + item.quantity.value * (item.effectivePrice?.amount ?? 0),
        0
      ),
    };
  })();

  return (
    <div className="bg-neut-light rounded-lg border border-neut-border shadow-neut-100 sticky top-0">
      <div className="px-6 py-4 border-b border-neut-border bg-neut-50">
        <h2 className="text-lg font-semibold text-neut-prim">
          {t('order_builder.order_calculator.title')}
        </h2>
        <p className="text-sm text-neut-sec mt-1">
          {t('order_builder.order_calculator.description')}
        </p>
      </div>

      {isCannedTally ? (
        <CannedTallyCalculatorContent
          platformProductLengths={platformProductLengths}
          orderInfo={orderInfo}
          onOrderInfoChange={onOrderInfoChange}
          averageNPrice={averageNPrice ?? 0}
          nCount={nCount}
          platformProductUnit={platformProductUnit}
          currency={currency}
          platformProductId={platformProductId}
          transportMode={transportMode}
          onTransportModeChange={onTransportModeChange}
          onTargetVehiclesChange={onTargetVehiclesChange}
          onInventorySummaryChange={setTallyInventorySummary}
          onTallyStockSummaryChange={setTallyStockSummary}
          onTallyNameChange={onTallyNameChange}
          carts={carts}
          t={t}
        />
      ) : (
        <RegularCalculatorContent
          orderInfo={orderInfo}
          onOrderInfoChange={onOrderInfoChange}
          onUserTargetChange={onUserTargetChange}
          onTargetOrderSizeBlur={onTargetOrderSizeBlur}
          onSuggestedWeeksToCover={onSuggestedWeeksToCover}
          platformProductUnit={platformProductUnit}
          averageNPrice={averageNPrice ?? 0}
          nCount={nCount}
          formattedMarketMidPriceWithUnit={formattedMarketMidPriceWithUnit}
          platformProductLengths={platformProductLengths}
          platformProductId={platformProductId}
          carts={carts}
          t={t}
        />
      )}

      {totalCartItems > 0 && (
        <div className="px-6 py-4 border-b border-neut-border">
          <h3
            data-testid="order-calculator-cart-count"
            className="text-sm font-semibold text-neut-900 mb-3"
          >
            {t('order_builder.order_calculator.cart_title', {
              count: totalCartItems,
            })}
          </h3>
          <div className="space-y-4" data-testid="order-calculator-carts">
            {carts?.map((cart) => {
              const cartTallyPlatformProductId = (cart.items ?? []).find(
                (i) => i.tallyPlatformProduct?.id
              )?.tallyPlatformProduct?.id;
              if (cartTallyPlatformProductId) {
                return (
                  <CannedTallyCartCard
                    key={cart.id}
                    items={cart.items ?? []}
                    organization={cart.sellingOrganization}
                    sellerOrganizationFacility={cart.sellerOrganizationFacility}
                    shippingLocationFacility={cart.shippingLocation.facility}
                    shippingLocationId={cart.shippingLocation.id}
                    id={cart.id}
                    parentOrderId={cart.parentOrder?.id}
                    isFromQuote={cart.isFromQuote}
                    incoTerm={cart.incoTerm}
                    transportVehicle={cart.transportVehicle}
                    platformProductId={cartTallyPlatformProductId}
                    calculatorTransportMode={
                      isCannedTally ? transportMode : undefined
                    }
                    currency={cart.currency}
                    inheritedIncoTerm={inheritedIncoTermFor(cart)}
                  />
                );
              }
              const nonTallyItems = (cart.items ?? []).filter(
                (i) => !i.tallyPlatformProduct?.id
              );
              if (nonTallyItems.length === 0) return null;
              return (
                <CartCard
                  key={cart.id}
                  items={nonTallyItems}
                  organization={cart.sellingOrganization}
                  sellerOrganizationFacility={cart.sellerOrganizationFacility}
                  shippingLocationFacility={cart.shippingLocation.facility}
                  shippingLocationId={cart.shippingLocation.id}
                  id={cart.id}
                  parentOrderId={cart.parentOrder?.id}
                  incoTerm={cart.incoTerm}
                  transportVehicle={cart.transportVehicle}
                  isInternational={cart.isInternational}
                  currency={cart.currency}
                  isFromQuote={cart.isFromQuote}
                  transportVehicleCatalog={transportVehicleCatalog}
                  inheritedTransport={inheritedTransportFor(cart)}
                />
              );
            })}
          </div>
        </div>
      )}
      <CartSummary
        platformProductUnit={platformProductUnit}
        targetSize={orderInfo.targetOrderSize ?? 0}
        actualWeeksCoverOverride={
          isCannedTally ? tallyInventorySummary?.avgWeeksCovered : null
        }
        targetWeeksOverride={
          isCannedTally ? tallyInventorySummary?.maxTargetWeeks : null
        }
        targetSizeOverride={
          isCannedTally ? tallyInventorySummary?.totalTargetQuantity : null
        }
        totalQuantityOverride={singleLengthCartTotals?.qty ?? null}
        totalValueOverride={singleLengthCartTotals?.value ?? null}
        tallyStockSummary={isCannedTally ? tallyStockSummary : null}
      />
    </div>
  );
}

function RegularCalculatorContent({
  orderInfo,
  onOrderInfoChange,
  onUserTargetChange,
  onTargetOrderSizeBlur,
  onSuggestedWeeksToCover,
  platformProductUnit,
  averageNPrice,
  nCount,
  formattedMarketMidPriceWithUnit,
  platformProductLengths,
  platformProductId,
  carts,
  t,
}: Readonly<{
  orderInfo: OrderInfo;
  onOrderInfoChange: (name: string, value: string) => void;
  onUserTargetChange?: () => void;
  onTargetOrderSizeBlur?: () => void;
  onSuggestedWeeksToCover: (weeks: number) => void;
  platformProductUnit: UnitCode;
  averageNPrice: number;
  nCount: number;
  formattedMarketMidPriceWithUnit: string | null;
  platformProductLengths: PlatformProductLength[];
  platformProductId?: string;
  carts: Cart[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}>) {
  const locationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );
  const lengthIds = platformProductLengths.map((ppl) => ppl.length.id);
  const inventoryResults = useGetBuyerInventoriesForLengths(
    lengthIds,
    platformProductId,
    locationId
  );

  const maxTargetWeeks = useMemo(() => {
    const weeks = inventoryResults
      .map((r, i) => {
        const inv = r?.data?.buyerInventoryByProductAndLocation;
        const stock = inv?.quantity?.value;
        const usageRate =
          inv?.buyerInventoryConfiguration?.usageRatePerWeek?.value;
        const targetWeeks =
          inv?.buyerInventoryConfiguration?.targetWeeksCovered;
        if (stock == null || !usageRate || targetWeeks == null) return null;
        const ppl = platformProductLengths[i];
        const ft = wholeFootLength(ppl?.length?.actualLengthInches ?? 0);
        const cartQty = carts
          .flatMap((cart) => cart.items ?? [])
          .filter((item) => {
            if (ft === null || getItemWholeFt(item) !== ft) return false;
            return (
              item.platformProductsLengths?.platformProduct?.id ===
                platformProductId ||
              item.tallyPlatformProduct?.id === platformProductId
            );
          })
          .reduce((sum, item) => sum + item.quantity.value, 0);
        const currentWeeks = (stock + cartQty) / usageRate;
        return Math.max(0, targetWeeks - currentWeeks);
      })
      .filter((w): w is number => w != null);
    return weeks.length > 0 ? Math.max(...weeks) : null;
  }, [inventoryResults, platformProductLengths, carts, platformProductId]);

  const onSuggestedWeeksToCoverRef = useRef(onSuggestedWeeksToCover);

  useEffect(() => {
    onSuggestedWeeksToCoverRef.current = onSuggestedWeeksToCover;
  }, [onSuggestedWeeksToCover]);

  useEffect(() => {
    if (maxTargetWeeks != null) {
      onSuggestedWeeksToCoverRef.current(maxTargetWeeks);
    }
  }, [maxTargetWeeks]);

  return (
    <div className="px-6 py-4 border-b border-neut-border space-y-4">
      <fieldset
        data-testid="target-order-size"
        className="flex items-end gap-2 border-0 p-0 m-0 min-w-0"
        onBlur={(e) => {
          if (
            !(e.relatedTarget instanceof HTMLInputElement) ||
            e.relatedTarget.name !== 'targetWeeksToCover'
          ) {
            onTargetOrderSizeBlur?.();
          }
        }}
      >
        <Input
          label={t('order_builder.order_calculator.target_order_size')}
          type="number"
          name={'targetOrderSize'}
          value={orderInfo.targetOrderSize?.toString() ?? ''}
          onChange={(name, value) => {
            onUserTargetChange?.();
            onOrderInfoChange(name, value);
          }}
        />
        <span className="text-sm text-neut-sec pb-3">
          {platformProductUnit}
        </span>
      </fieldset>
      <div className="flex items-end gap-2">
        <Input
          label={t('order_builder.order_calculator.target_weeks_to_cover')}
          type="number"
          name={'targetWeeksToCover'}
          value={orderInfo.targetWeeksToCover?.toString()}
          onChange={(name, value) => {
            onUserTargetChange?.();
            onOrderInfoChange(name, value);
          }}
        />
        <span className="text-sm text-neut-sec pb-3">
          {t('order_builder.order_calculator.units.weeks')}
        </span>
      </div>
      <div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col w-full gap-2">
            <Input
              leftIcon={<span className="text-neut-prim">$</span>}
              type="number"
              name="targetPrice"
              min={MIN_PRICE}
              max={MAX_PRICE}
              value={orderInfo.targetPrice?.toString()}
              onChange={onOrderInfoChange}
              label={
                <span className="ml-md mb-xxs text-sm font-medium text-neut-prim">
                  {t('order_builder.order_calculator.target_price')}{' '}
                  {formattedMarketMidPriceWithUnit && (
                    <MarketPricePill price={formattedMarketMidPriceWithUnit} />
                  )}
                </span>
              }
            />
          </div>
          <span className="text-sm text-neut-sec pb-3">
            /{platformProductUnit}
          </span>
        </div>
        <p className="text-xs text-neut-500 mt-1">
          {t('order_builder.order_calculator.recommended_price', {
            averagePrice: formatUnitAmount(averageNPrice),
            count: nCount,
            unit: platformProductUnit,
          })}
        </p>
      </div>
    </div>
  );
}

type TrucksToGreenParams = {
  activeLengths: number[];
  inventoryResults: ReturnType<typeof useGetBuyerInventoriesForLengths>;
  tallyBundlesByFt: Record<number, number>;
  vehicleCapacities: Parameters<typeof getVehiclePayload>[0] | undefined;
  piecesPerBundle: number;
  platformProductUnit: UnitCode;
  widthInches: number;
  thicknessInches: number;
  carts: Cart[];
  platformProductId: string | undefined;
  transportVehicleId: string | undefined;
};

function computeTrucksToGreen({
  activeLengths,
  inventoryResults,
  tallyBundlesByFt,
  vehicleCapacities,
  piecesPerBundle,
  platformProductUnit,
  widthInches,
  thicknessInches,
  carts,
  platformProductId,
  transportVehicleId,
}: TrucksToGreenParams): number | null {
  if (!piecesPerBundle || !widthInches || !thicknessInches) return null;

  const unitsPerBundleByFt: Record<number, number> = {};
  let unitsPerTally = 0;
  for (const ft of activeLengths) {
    const tallyBundles = tallyBundlesByFt[ft];
    if (!tallyBundles) continue;
    try {
      const upb = convertPiecesToUnit(
        piecesPerBundle,
        platformProductUnit,
        ft * 12,
        widthInches,
        thicknessInches
      );
      unitsPerBundleByFt[ft] = upb;
      unitsPerTally += tallyBundles * upb;
    } catch {
      /* skip length */
    }
  }

  const vehicleCapacityValue = vehicleCapacities
    ? (getVehiclePayload(vehicleCapacities, transportVehicleId ?? '')?.value ??
      null)
    : null;
  const effectiveCapacity = vehicleCapacityValue ?? unitsPerTally;
  const talliesPerTruck =
    unitsPerTally > 0 ? effectiveCapacity / unitsPerTally : 1;

  let maxTrucks = 0;
  for (let i = 0; i < activeLengths.length; i++) {
    const ft = activeLengths[i];
    const inv = inventoryResults[i]?.data?.buyerInventoryByProductAndLocation;
    const stock = inv?.quantity?.value;
    const usageRate = inv?.buyerInventoryConfiguration?.usageRatePerWeek?.value;
    const targetWeeks = inv?.buyerInventoryConfiguration?.targetWeeksCovered;
    const tallyBundles = tallyBundlesByFt[ft];
    const unitsPerBundle = unitsPerBundleByFt[ft];
    if (
      stock == null ||
      !usageRate ||
      !targetWeeks ||
      !tallyBundles ||
      !unitsPerBundle
    )
      continue;

    const cartUnitsAtFt = carts
      .flatMap((cart) => cart.items ?? [])
      .filter(
        (item) =>
          item.tallyPlatformProduct?.id === platformProductId &&
          getItemWholeFt(item) === ft
      )
      .reduce((sum, item) => sum + item.quantity.value, 0);

    const currentWeeks = (stock + cartUnitsAtFt) / usageRate;
    const weeksRequired = Math.max(0, targetWeeks - currentWeeks);
    if (weeksRequired === 0) continue;

    const quantityRequired = weeksRequired * usageRate;
    const bundlesRequired = quantityRequired / unitsPerBundle;
    const talliesRequired = Math.ceil(bundlesRequired / tallyBundles);
    const trucksRequired = talliesRequired / talliesPerTruck;
    maxTrucks = Math.max(maxTrucks, trucksRequired);
  }
  return Math.ceil(maxTrucks * 2) / 2;
}

function computeTallyInventorySummary(
  activeLengths: number[],
  inventoryResults: ReturnType<typeof useGetBuyerInventoriesForLengths>,
  carts: Cart[],
  platformProductId: string | undefined
): TallyInventorySummary | null {
  let totalWeeks = 0;
  let maxTargetWeeks = 0;
  let totalTargetQty = 0;
  let lengthCount = 0;
  for (let i = 0; i < activeLengths.length; i++) {
    const ft = activeLengths[i];
    const inv = inventoryResults[i]?.data?.buyerInventoryByProductAndLocation;
    const stock = inv?.quantity?.value;
    const usageRate = inv?.buyerInventoryConfiguration?.usageRatePerWeek?.value;
    const targetWeeks = inv?.buyerInventoryConfiguration?.targetWeeksCovered;
    if (stock == null || !usageRate) continue;
    const cartQtyAtFt = carts
      .flatMap((cart) => cart.items ?? [])
      .filter(
        (item) =>
          item.tallyPlatformProduct?.id === platformProductId &&
          getItemWholeFt(item) === ft
      )
      .reduce((sum, item) => sum + item.quantity.value, 0);
    totalWeeks += (stock + cartQtyAtFt) / usageRate;
    lengthCount++;
    if (targetWeeks != null && targetWeeks > 0) {
      const missingQty = Math.max(
        0,
        targetWeeks * usageRate - stock - cartQtyAtFt
      );
      if (missingQty > 0) {
        maxTargetWeeks = Math.max(maxTargetWeeks, targetWeeks);
        totalTargetQty += missingQty;
      }
    }
  }
  return lengthCount > 0
    ? {
        avgWeeksCovered: totalWeeks / lengthCount,
        maxTargetWeeks,
        totalTargetQuantity: totalTargetQty,
      }
    : null;
}

function computeTallyStockSummary(
  activeLengths: number[],
  inventoryResults: ReturnType<typeof useGetBuyerInventoriesForLengths>,
  carts: Cart[],
  platformProductId: string | undefined,
  totalUsageRate: number
): TallyStockSummary | null {
  const byLength = activeLengths
    .map((ft, i) => {
      const inv = inventoryResults[i]?.data?.buyerInventoryByProductAndLocation;
      const rawStock = inv?.quantity?.value ?? null;
      const usageRate =
        inv?.buyerInventoryConfiguration?.usageRatePerWeek?.value ?? null;
      const targetWeeks =
        inv?.buyerInventoryConfiguration?.targetWeeksCovered ?? null;
      if (rawStock == null) return null;
      const cartQtyAtFt = carts
        .flatMap((cart) => cart.items ?? [])
        .filter(
          (item) =>
            getItemWholeFt(item) === ft &&
            (item.tallyPlatformProduct?.id === platformProductId ||
              item.platformProductsLengths?.platformProduct?.id ===
                platformProductId)
        )
        .reduce((sum, item) => sum + item.quantity.value, 0);
      const effectiveStock = rawStock + cartQtyAtFt;
      const weeksCovered = usageRate ? effectiveStock / usageRate : null;
      return {
        ft,
        stock: effectiveStock,
        weeksCovered,
        targetWeeks,
      } as TallyStockEntry;
    })
    .filter((e): e is TallyStockEntry => e !== null);
  if (byLength.length === 0) return null;
  return { byLength, usageRate: totalUsageRate > 0 ? totalUsageRate : null };
}

function CannedTallyCalculatorContent({
  platformProductLengths,
  orderInfo,
  onOrderInfoChange,
  averageNPrice,
  nCount,
  platformProductUnit,
  currency,
  platformProductId,
  transportMode = 'trucks',
  onTransportModeChange,
  onTargetVehiclesChange,
  onInventorySummaryChange,
  onTallyStockSummaryChange,
  onTallyNameChange,
  carts,
  t,
}: Readonly<{
  platformProductLengths: PlatformProductLength[];
  orderInfo: OrderInfo;
  onOrderInfoChange: (name: string, value: string) => void;
  averageNPrice: number;
  nCount: number;
  platformProductUnit: UnitCode;
  currency?: string;
  platformProductId: string | undefined;
  transportMode?: 'trucks' | 'railcars';
  onTransportModeChange?: (mode: 'trucks' | 'railcars') => void;
  onTargetVehiclesChange?: (v: number) => void;
  onInventorySummaryChange?: (summary: TallyInventorySummary | null) => void;
  onTallyStockSummaryChange?: (summary: TallyStockSummary | null) => void;
  onTallyNameChange?: (name: string | undefined) => void;
  carts: Cart[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}>) {
  const [[tallyModeForName, selectedTallyName], setTallyState] = useState<
    ['trucks' | 'railcars', string | undefined]
  >([transportMode, undefined]);

  const currencySymbol =
    (currency ? currencyCodeToSymbolMap[currency] : undefined) ?? '$';

  const activeLengths =
    transportMode === 'railcars' ? RAILCAR_LENGTHS_FT : TRUCK_LENGTHS_FT;

  const { data: vehiclesData } = useGetTransportVehicles();
  const vehicleName =
    CANNED_TALLY_VEHICLE_NAMES[transportMode === 'railcars' ? 1 : 0];
  const transportVehicleId = vehiclesData?.transportVehicles?.find(
    (v) => v.name === vehicleName
  )?.id;

  const { data: tallyData, isLoading: isTallyLoading } =
    useGetPlatformProductCannedTallies(platformProductId, transportVehicleId);
  const isTallyPending = !transportVehicleId || isTallyLoading;
  const tallies = tallyData?.platformProductCannedTallies ?? [];
  const tallyNameForCurrentMode =
    tallyModeForName === transportMode ? selectedTallyName : undefined;
  const effectiveTallyName =
    tallies.find((tally) => tally.name === tallyNameForCurrentMode)?.name ??
    tallies[0]?.name;
  const selectedTally = tallies.find(
    (tally) => tally.name === effectiveTallyName
  );

  const tallyBundlesByFt = useMemo(
    () => buildTallyBundlesByFt(selectedTally?.bundles),
    [selectedTally]
  );

  const locationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );
  const { data: productData } = useGetPlatformProductById(
    platformProductId ?? ''
  );
  const product = productData?.platformProduct;

  const piecesPerBundle = product?.piecesPerBundle?.value ?? 0;
  const { widthInches, thicknessInches } = getPlatformProductDimensions(
    product,
    platformProductLengths[0]?.length,
    platformProductLengths[0]?.isNominal
  );

  const standardUnit = product?.standardUnitsCode;
  const { lengthIdByFt, lengthDataByFt } = useMemo(() => {
    const idMap = new Map<number, string>();
    const dataMap = new Map<
      number,
      { midPrice: number | null; unit: UnitCode }
    >();
    const addLengths = (wholeFeetOnly: boolean) => {
      for (const ppl of platformProductLengths) {
        const inches = ppl.length.actualLengthInches;
        if (wholeFeetOnly && inches % 12 !== 0) continue;
        const ft = Math.round(inches / 12);
        if (activeLengths.includes(ft) && !dataMap.has(ft)) {
          const { amount, unit } = marketMidPriceIn(
            ppl.latestPriceMid?.amount,
            platformProductUnit,
            standardUnit,
            getPlatformProductDimensions(product, ppl.length, ppl.isNominal)
          );
          dataMap.set(ft, { midPrice: amount, unit });
          idMap.set(ft, ppl.length.id);
        }
      }
    };
    addLengths(true);
    addLengths(false);
    return { lengthIdByFt: idMap, lengthDataByFt: dataMap };
  }, [
    platformProductLengths,
    activeLengths,
    standardUnit,
    platformProductUnit,
    product,
  ]);

  const activeLengthIds = useMemo(
    () => activeLengths.map((ft) => lengthIdByFt.get(ft) ?? ''),
    [activeLengths, lengthIdByFt]
  );
  const inventoryResults = useGetBuyerInventoriesForLengths(
    activeLengthIds,
    platformProductId,
    locationId
  );

  const trucksToGreen = useMemo(
    () =>
      computeTrucksToGreen({
        activeLengths,
        inventoryResults,
        tallyBundlesByFt,
        vehicleCapacities: product?.platformProductVehicleCapacities,
        piecesPerBundle,
        platformProductUnit,
        widthInches,
        thicknessInches,
        carts,
        platformProductId,
        transportVehicleId,
      }),
    [
      activeLengths,
      inventoryResults,
      tallyBundlesByFt,
      product,
      piecesPerBundle,
      platformProductUnit,
      widthInches,
      thicknessInches,
      carts,
      platformProductId,
      transportVehicleId,
    ]
  );

  const autoTargetVehicles = useMemo(
    () => Math.max(0, trucksToGreen ?? 0),
    [trucksToGreen]
  );

  const [manualTargetVehicles, setManualTargetVehicles] = useState<
    number | null
  >(null);
  const targetVehicles = manualTargetVehicles ?? autoTargetVehicles;

  useEffect(() => {
    onTargetVehiclesChange?.(targetVehicles);
  }, [targetVehicles, onTargetVehiclesChange]);

  const tallyInventorySummary = useMemo(
    () =>
      computeTallyInventorySummary(
        activeLengths,
        inventoryResults,
        carts,
        platformProductId
      ),
    [activeLengths, inventoryResults, carts, platformProductId]
  );

  const onInventorySummaryChangeRef = useRef(onInventorySummaryChange);
  onInventorySummaryChangeRef.current = onInventorySummaryChange;

  const onTallyNameChangeRef = useRef(onTallyNameChange);
  onTallyNameChangeRef.current = onTallyNameChange;

  useEffect(() => {
    onTallyNameChangeRef.current?.(effectiveTallyName);
  }, [effectiveTallyName]);

  useEffect(() => {
    onInventorySummaryChangeRef.current?.(tallyInventorySummary);
    // Depend on primitives, not the object reference, to avoid infinite re-render loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tallyInventorySummary?.avgWeeksCovered,
    tallyInventorySummary?.maxTargetWeeks,
    tallyInventorySummary?.totalTargetQuantity,
  ]);

  const usageRateByFt: Record<number, number | null> = Object.fromEntries(
    activeLengths.map((ft, i) => [
      ft,
      inventoryResults[i]?.data?.buyerInventoryByProductAndLocation
        ?.buyerInventoryConfiguration?.usageRatePerWeek?.value ?? null,
    ])
  );

  const weeksCoveredPerFt: Record<number, number | null> = Object.fromEntries(
    activeLengths.map((ft, i) => {
      const inv = inventoryResults[i]?.data?.buyerInventoryByProductAndLocation;
      const target =
        inv?.buyerInventoryConfiguration?.targetWeeksCovered ?? null;
      const stock = inv?.quantity?.value ?? null;
      const usageRate =
        inv?.buyerInventoryConfiguration?.usageRatePerWeek?.value;
      if (target == null || stock == null || !usageRate) return [ft, null];
      const cartQtyAtFt = carts
        .flatMap((cart) => cart.items ?? [])
        .filter(
          (item) =>
            item.tallyPlatformProduct?.id === platformProductId &&
            getItemWholeFt(item) === ft
        )
        .reduce((sum, item) => sum + item.quantity.value, 0);
      const currentWeeks = (stock + cartQtyAtFt) / usageRate;
      return [ft, Math.max(0, target - currentWeeks)];
    })
  );

  const definedWeeks = activeLengths
    .map((ft) => weeksCoveredPerFt[ft])
    .filter((w): w is number => w != null);
  const avgWeeks =
    definedWeeks.length > 0
      ? definedWeeks.reduce((a, b) => a + b, 0) / definedWeeks.length
      : null;

  const totalUsageRate = activeLengths
    .map((ft) => usageRateByFt[ft])
    .filter((r): r is number => r != null)
    .reduce((a, b) => a + b, 0);

  const tallyStockSummary = useMemo(
    () =>
      computeTallyStockSummary(
        activeLengths,
        inventoryResults,
        carts,
        platformProductId,
        totalUsageRate
      ),
    [activeLengths, inventoryResults, carts, platformProductId, totalUsageRate]
  );

  const onTallyStockSummaryChangeRef = useRef(onTallyStockSummaryChange);
  onTallyStockSummaryChangeRef.current = onTallyStockSummaryChange;

  const tallyStockSummaryKey =
    tallyStockSummary?.byLength.map((e) => `${e.ft}:${e.stock}`).join(',') ??
    '';

  useEffect(() => {
    onTallyStockSummaryChangeRef.current?.(tallyStockSummary);
    // Depend on the derived key string, not the object reference, to avoid infinite re-render loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tallyStockSummaryKey]);

  const pricedLengths = activeLengths
    .map((ft) => lengthDataByFt.get(ft))
    .filter(
      (d): d is { midPrice: number; unit: UnitCode } =>
        d != null && d.midPrice != null
    );
  const priceUnit =
    pricedLengths.length > 0 ? pricedLengths[0].unit : platformProductUnit;
  const rlMidPrice =
    pricedLengths.length > 0
      ? pricedLengths.reduce((sum, d) => sum + d.midPrice, 0) /
        pricedLengths.length
      : null;

  const rlPrice = formatPricedMarketMid(
    { amount: rlMidPrice, unit: priceUnit },
    currency
  );

  const bundlesLabel =
    transportMode === 'trucks'
      ? t('order_builder.canned_tally_calculator.bundles_per_truck')
      : t('order_builder.canned_tally_calculator.bundles_per_railcar');

  const targetVehiclesLabel =
    transportMode === 'trucks'
      ? t('order_builder.canned_tally_calculator.target_trucks_label')
      : t('order_builder.canned_tally_calculator.target_railcars_label');

  return (
    <div className="px-6 py-4 space-y-4 border-b border-neut-border">
      {/* Transport Mode */}
      <div>
        <label className="block text-xs font-medium text-neut-700 mb-2">
          {t('order_builder.canned_tally_calculator.transport_mode')}
        </label>
        <div className="inline-flex items-center gap-1 bg-neut-light border border-neut-300 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => onTransportModeChange?.('trucks')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1 whitespace-nowrap ${
              transportMode === 'trucks'
                ? 'bg-prim text-neut-light'
                : 'text-neut-600 hover:bg-neut-100'
            }`}
          >
            <Truck size={14} />
            {t('order_builder.canned_tally_calculator.trucks')}
          </button>
          <button
            type="button"
            onClick={() => onTransportModeChange?.('railcars')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1 whitespace-nowrap ${
              transportMode === 'railcars'
                ? 'bg-prim text-neut-light'
                : 'text-neut-600 hover:bg-neut-100'
            }`}
          >
            <TramFront size={14} />
            {t('order_builder.canned_tally_calculator.railcars')}
          </button>
        </div>
      </div>

      {/* Tally Name Buttons */}
      {tallies.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {tallies.map((tally) => (
            <button
              key={tally.name}
              type="button"
              onClick={() => setTallyState([transportMode, tally.name])}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                tally.name === effectiveTallyName
                  ? 'bg-prim text-neut-light border-prim'
                  : 'bg-neut-light text-neut-600 border-neut-300 hover:bg-neut-100'
              }`}
            >
              {tally.name}
            </button>
          ))}
        </div>
      )}

      {/* Bundles per Truck/Railcar */}
      <div>
        <label className="block text-xs font-medium text-neut-700 mb-2">
          {bundlesLabel}
        </label>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {TRUCK_LENGTHS_FT.map((ft) => (
              <div key={ft} className="flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-neut-600">
                  {ft}
                  {t('order_builder.canned_tally_calculator.ft')}
                </span>
                <span className="w-16 px-2 py-1.5 text-sm text-center bg-neut-100 rounded-md text-neut-700 font-medium">
                  {isTallyPending ? '...' : (tallyBundlesByFt[ft] ?? '—')}
                </span>
              </div>
            ))}
          </div>
          {transportMode === 'railcars' && (
            <div className="flex items-center gap-2">
              {[18, 20].map((ft) => (
                <div key={ft} className="flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-neut-600">
                    {ft}
                    {t('order_builder.canned_tally_calculator.ft')}
                  </span>
                  <span className="w-16 px-2 py-1.5 text-sm text-center bg-neut-100 rounded-md text-neut-700 font-medium">
                    {isTallyPending ? '...' : (tallyBundlesByFt[ft] ?? '—')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Target Number of Trucks/Railcars */}
      <div>
        <label
          htmlFor="rt-target-vehicles"
          className="block text-xs font-medium text-neut-700 mb-2"
        >
          {targetVehiclesLabel}
        </label>
        <div className="flex items-center gap-2">
          <input
            id="rt-target-vehicles"
            type="number"
            min="0.01"
            step="0.01"
            className="flex-1 px-3 py-2 border border-neut-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prim"
            value={targetVehicles}
            onChange={(e) => setManualTargetVehicles(Number(e.target.value))}
          />
          <span className="text-sm text-neut-600">
            {t(
              transportMode === 'trucks'
                ? 'order_builder.canned_tally_calculator.trucks'
                : 'order_builder.canned_tally_calculator.railcars'
            )}
          </span>
        </div>
        <p className="text-xs text-neut-500 mt-1">
          {t('order_builder.canned_tally_calculator.trucks_hint')}
        </p>
      </div>

      {/* Weeks Covered per Length */}
      <div>
        <label className="block text-xs font-medium text-neut-700 mb-2">
          {t('order_builder.canned_tally_calculator.target_weeks_per_length')}
        </label>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {TRUCK_LENGTHS_FT.map((ft) => (
              <div key={ft} className="flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-neut-600">
                  {ft}
                  {t('order_builder.canned_tally_calculator.ft')}
                </span>
                <span className="w-16 px-2 py-1.5 text-sm text-center bg-neut-100 rounded-md text-neut-700 font-medium">
                  {weeksCoveredPerFt[ft] != null
                    ? weeksCoveredPerFt[ft].toFixed(1)
                    : '—'}
                </span>
              </div>
            ))}
          </div>
          {transportMode === 'railcars' && (
            <div className="flex items-center gap-2">
              {[18, 20].map((ft) => (
                <div key={ft} className="flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-neut-600">
                    {ft}
                    {t('order_builder.canned_tally_calculator.ft')}
                  </span>
                  <span className="w-16 px-2 py-1.5 text-sm text-center bg-neut-100 rounded-md text-neut-700 font-medium">
                    {weeksCoveredPerFt[ft] != null
                      ? weeksCoveredPerFt[ft].toFixed(1)
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {avgWeeks != null && (
          <p className="text-xs text-neut-500 mt-1">
            {t('order_builder.canned_tally_calculator.avg_weeks', {
              avg: avgWeeks.toFixed(1),
            })}
          </p>
        )}
        {totalUsageRate > 0 && (
          <p className="text-xs text-neut-500 mt-1">
            {t('order_builder.canned_tally_calculator.total_usage_rate', {
              rate: totalUsageRate.toFixed(2),
              unit: platformProductUnit,
            })}
          </p>
        )}
      </div>

      {/* Market Price + Target Price */}
      <div>
        {rlPrice != null && (
          <>
            <label className="block text-xs font-medium text-neut-700 mb-2">
              {t('order_builder.canned_tally_calculator.market_price')}
            </label>
            <div className="flex items-center gap-2 mb-2">
              <MarketPricePill
                label={`${t('order_builder.canned_tally_calculator.rl_label')}:`}
                price={rlPrice}
              />
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {activeLengths.map((ft) => {
                const lengthData = lengthDataByFt.get(ft);
                if (lengthData == null || lengthData.midPrice == null)
                  return null;
                return (
                  <span
                    key={ft}
                    className="px-1.5 py-0.5 text-[10px] bg-neut-100 text-neut-600 rounded font-medium whitespace-nowrap"
                  >
                    {ft}
                    {t('order_builder.canned_tally_calculator.ft')}:{' '}
                    {formatUnitPrice(lengthData.midPrice, currency)}/
                    {priceUnitLabel(lengthData.unit)}
                  </span>
                );
              })}
            </div>
          </>
        )}
        <label
          htmlFor="rt-target-price"
          className="block text-xs font-medium text-neut-700 mb-2"
        >
          {t('order_builder.order_calculator.target_price')}
        </label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-neut-600">{currencySymbol}</span>
          <input
            id="rt-target-price"
            type="number"
            name="targetPrice"
            min={MIN_PRICE}
            max={MAX_PRICE}
            className="flex-1 px-3 py-2 border border-neut-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-prim"
            value={orderInfo.targetPrice?.toString()}
            onChange={(e) => onOrderInfoChange('targetPrice', e.target.value)}
          />
          <span className="text-sm text-neut-600">/{platformProductUnit}</span>
        </div>
        <p className="text-xs text-neut-500 mt-1">
          {t('order_builder.order_calculator.recommended_price', {
            averagePrice: averageNPrice.toFixed(2),
            count: nCount,
            unit: platformProductUnit,
          })}
        </p>
      </div>
    </div>
  );
}
